import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  createBlockResponse,
  createCriterionResponse,
  createEvidence,
  getAssignment,
  getRun,
  linkEvidence,
  listBlockResponses,
  listCriterionResponses,
  listEvidence,
  listEvidenceLinks,
  listInstrumentBlocks,
  listInstrumentCriteria,
  listInstrumentTriggers,
  listScaleAnchors,
  myAuthority,
  unlinkEvidence,
  updateBlockResponse,
  updateCriterionResponse,
} from "../lib/api";
import { supabase } from "../lib/supabase";
import { offersNotApplicable } from "../lib/applicability";
import type { ScaleLevel } from "../lib/api";
import type {
  Applicability,
  BlockResponse,
  CriterionResponse,
  EvidenceKind,
  EvidenceReference,
  InstrumentBlock,
  InstrumentCriterion,
  InstrumentTrigger,
  ResponseEvidenceLink,
  Run,
} from "../lib/types";
import { useBreadcrumb } from "../shell/ContextHeader";
import { NoNamesHint } from "./NoNamesHint";
import { blockNoun, capitalised, NEUTRAL_BLOCK_NOUN, type BlockNoun } from "../lib/blockNoun";

// The assessment workspace: the blocks and criteria of the run's PINNED
// instrument snapshot, however many it declares. What this screen must get right is mostly what it must
// be UNABLE to do:
//
//   - it renders from run.instrument_version_id, which no client can change
//     (0004 column grants) - reloading re-reads the pin, so it cannot drift
//     to a newer version (gates 1, 2)
//   - response rows are created on FIRST EDIT, never on open (S2)
//   - unscored is null and stays null; "0" is a score the assessor chose
//     (gate 3); the score control disables unless applicability is
//     `assessed` (gate 4, mirroring the DB constraint)
//   - `combined` demands a destination before it saves (gate 5)
//   - "Not applicable" is not offered on an `na_prohibited` criterion, nor on
//     an artifact that has one - mirroring 0022 (architect Q5 as corrected),
//     never replacing it; `mandatory` still permits it
//   - trigger confirmation is three-state: not asked / no / yes (gate 6)
//   - editing is enabled only for the assigned assessor or owner/lead
//     override, on an active run - mirroring 0008, never replacing it
//   - evidence URLs open only on a user click, with noopener; textual
//     locators are never rendered as links (ADR-32)

// "Combined" names the instrument's own block noun (E-F12), not always
// "artifact".
function applicabilityLabel(a: Applicability, noun: BlockNoun): string {
  switch (a) {
    case "assessed": return "Assessed";
    case "combined": return `Combined into another ${noun.one}`;
    case "not_applicable": return "Not applicable";
    case "not_assessed": return "Not assessed";
    case "excluded": return "Excluded";
  }
}
const APPLICABILITY_ORDER: Applicability[] = [
  "assessed",
  "combined",
  "not_applicable",
  "not_assessed",
  "excluded",
];

type SaveState = { kind: "idle" } | { kind: "saving" } | { kind: "error"; message: string };

type Loaded = {
  run: Run;
  editable: boolean;
  readOnlyReason: string | null;
  blocks: InstrumentBlock[];
  criteria: Map<string, InstrumentCriterion[]>; // block id -> criteria
  triggers: Map<string, InstrumentTrigger>;     // block id -> trigger
  blockResponses: Map<string, BlockResponse>;   // block_key -> response
  criterionResponses: Map<string, CriterionResponse>; // blockKey/criterionKey
  evidence: EvidenceReference[];
  links: ResponseEvidenceLink[];
  anchors: Map<string, ScaleLevel[]>;
};

const ck = (blockKey: string, criterionKey: string) => `${blockKey}/${criterionKey}`;

export function AssessmentWorkspace() {
  const { orgId, runId } = useParams<{ orgId: string; runId: string }>();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | ({ kind: "ready" } & Loaded)
  >({ kind: "loading" });
  // ?block=<block_key> deep-links a specific artifact - the results screen
  // navigates here from its per-artifact rows (session 5, "navigation from
  // summary to relevant sections"). Read once as the initial selection.
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(
    searchParams.get("block"),
  );
  const [save, setSave] = useState<SaveState>({ kind: "idle" });

  const load = useCallback(async () => {
    if (!orgId || !runId) return;
    try {
      const run = await getRun(runId);
      if (!run || run.organisation_id !== orgId) {
        setState({ kind: "error", message: "run not found, or you are not a member" });
        return;
      }
      const [assignment, role, blocks, criteria, triggers, blockRs, critRs, evidence, links, anchors, userData] =
        await Promise.all([
          getAssignment(runId),
          myAuthority(orgId),
          listInstrumentBlocks(run.instrument_version_id),
          listInstrumentCriteria(run.instrument_version_id),
          listInstrumentTriggers(run.instrument_version_id),
          listBlockResponses(runId),
          listCriterionResponses(runId),
          listEvidence(runId),
          listEvidenceLinks(orgId),
          listScaleAnchors(run.instrument_version_id),
          supabase.auth.getUser(),
        ]);

      const me = userData.data.user?.id ?? null;
      const active = run.state === "draft" || run.state === "ready_for_review";
      const isAssigned = me !== null && me === assignment?.assessor_id;
      const isOverride = role === "owner" || role === "lead_assessor";
      const editable = active && (isAssigned || isOverride);
      const readOnlyReason = editable
        ? null
        : !active
          ? `this run is ${run.state}; assessment data is read-only`
          : "you are not the assigned assessor (an owner or lead assessor may override)";

      const critByBlock = new Map<string, InstrumentCriterion[]>();
      for (const c of criteria) {
        const list = critByBlock.get(c.instrument_block_id) ?? [];
        list.push(c);
        critByBlock.set(c.instrument_block_id, list);
      }
      const trigByBlock = new Map<string, InstrumentTrigger>();
      for (const t of triggers) trigByBlock.set(t.instrument_block_id, t);

      const blockRespByKey = new Map<string, BlockResponse>();
      for (const r of blockRs) blockRespByKey.set(r.block_id, r);
      const critRespByKey = new Map<string, CriterionResponse>();
      const blockKeyById = new Map<string, string>();
      for (const r of blockRs) blockKeyById.set(r.id, r.block_id);
      for (const r of critRs) {
        const bk = blockKeyById.get(r.block_response_id);
        if (bk) critRespByKey.set(ck(bk, r.criterion_id), r);
      }

      setState({
        kind: "ready",
        run,
        editable,
        readOnlyReason,
        blocks,
        criteria: critByBlock,
        triggers: trigByBlock,
        blockResponses: blockRespByKey,
        criterionResponses: critRespByKey,
        evidence,
        links,
        anchors,
      });
      setSelected((s) => s ?? blocks[0]?.block_key ?? null);
      setTrail([
        { label: "Clients & engagements", to: "/clients" },
        { label: "Run", to: `/clients/${orgId}/runs/${runId}` },
        { label: run.title ?? "Untitled run" },
        { label: "Assess" },
      ]);
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [orgId, runId, setTrail]);

  useEffect(() => {
    void load();
  }, [load]);

  // ---- mutation helpers: ensure-on-first-edit, then patch, then refresh
  // local caches from the row the database returned (deterministic save).

  const ensureBlock = useCallback(
    async (s: Loaded, blockKey: string): Promise<BlockResponse> => {
      const existing = s.blockResponses.get(blockKey);
      if (existing) return existing;
      return createBlockResponse(s.run.organisation_id, s.run.id, blockKey);
    },
    [],
  );

  const patchBlock = useCallback(
    async (blockKey: string, patch: Parameters<typeof updateBlockResponse>[1]) => {
      if (state.kind !== "ready") return;
      setSave({ kind: "saving" });
      try {
        const base = await ensureBlock(state, blockKey);
        const updated = await updateBlockResponse(base.id, patch);
        setState((prev) => {
          if (prev.kind !== "ready") return prev;
          const next = new Map(prev.blockResponses);
          next.set(blockKey, updated);
          return { ...prev, blockResponses: next };
        });
        setSave({ kind: "idle" });
      } catch (e) {
        setSave({ kind: "error", message: (e as Error).message });
      }
    },
    [state, ensureBlock],
  );

  const patchCriterion = useCallback(
    async (
      blockKey: string,
      criterionKey: string,
      patch: Parameters<typeof updateCriterionResponse>[1],
    ) => {
      if (state.kind !== "ready") return;
      setSave({ kind: "saving" });
      try {
        const blockResp = await ensureBlock(state, blockKey);
        const key = ck(blockKey, criterionKey);
        let critResp = state.criterionResponses.get(key);
        if (!critResp) {
          critResp = await createCriterionResponse(
            state.run.organisation_id,
            state.run.id,
            blockResp.id,
            criterionKey,
          );
        }
        const updated = await updateCriterionResponse(critResp.id, patch);
        setState((prev) => {
          if (prev.kind !== "ready") return prev;
          const nextB = new Map(prev.blockResponses);
          nextB.set(blockKey, blockResp);
          const nextC = new Map(prev.criterionResponses);
          nextC.set(key, updated);
          return { ...prev, blockResponses: nextB, criterionResponses: nextC };
        });
        setSave({ kind: "idle" });
      } catch (e) {
        setSave({ kind: "error", message: (e as Error).message });
      }
    },
    [state, ensureBlock],
  );

  const detachEvidence = useCallback(
    async (responseId: string, evidenceId: string) => {
      setSave({ kind: "saving" });
      try {
        await unlinkEvidence(responseId, evidenceId);
        await load();
        setSave({ kind: "idle" });
      } catch (e) {
        setSave({ kind: "error", message: (e as Error).message });
      }
    },
    [load],
  );

  const attachEvidence = useCallback(
    async (blockKey: string, criterionKey: string, evidenceId: string) => {
      if (state.kind !== "ready") return;
      setSave({ kind: "saving" });
      try {
        const blockResp = await ensureBlock(state, blockKey);
        const key = ck(blockKey, criterionKey);
        let critResp = state.criterionResponses.get(key);
        if (!critResp) {
          critResp = await createCriterionResponse(
            state.run.organisation_id,
            state.run.id,
            blockResp.id,
            criterionKey,
          );
        }
        await linkEvidence(state.run.organisation_id, evidenceId, critResp.id);
        await load();
        setSave({ kind: "idle" });
      } catch (e) {
        setSave({ kind: "error", message: (e as Error).message });
      }
    },
    [state, ensureBlock, load],
  );

  const addEvidence = useCallback(
    async (input: { evidence_type: string; label: string; reference_kind: EvidenceKind; locator_original: string | null }) => {
      if (state.kind !== "ready") return;
      setSave({ kind: "saving" });
      try {
        const created = await createEvidence({
          organisation_id: state.run.organisation_id,
          run_id: state.run.id,
          ...input,
        });
        setState((prev) =>
          prev.kind === "ready" ? { ...prev, evidence: [...prev.evidence, created] } : prev,
        );
        setSave({ kind: "idle" });
      } catch (e) {
        setSave({ kind: "error", message: (e as Error).message });
      }
    },
    [state],
  );

  // ---- completion, computed from responses (indicator only - the kernel's
  // coverage computation is the authority, later).

  const progress = useMemo(() => {
    if (state.kind !== "ready") return new Map<string, { done: number; total: number }>();
    const m = new Map<string, { done: number; total: number }>();
    for (const b of state.blocks) {
      const crits = state.criteria.get(b.id) ?? [];
      const blockResp = state.blockResponses.get(b.block_key);
      if (blockResp && blockResp.applicability !== "assessed") {
        m.set(b.block_key, { done: crits.length, total: crits.length });
        continue;
      }
      let done = 0;
      for (const c of crits) {
        const r = state.criterionResponses.get(ck(b.block_key, c.criterion_key));
        if (r && (r.score !== null || r.applicability !== "assessed")) done += 1;
      }
      m.set(b.block_key, { done, total: crits.length });
    }
    return m;
  }, [state]);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error")
    return <div className="error-banner">Could not load: {state.message}</div>;

  const s = state;
  const noun = blockNoun(s.blocks.map((b) => b.kind));
  const current = s.blocks.find((b) => b.block_key === selected) ?? s.blocks[0];
  if (!current) return <div className="error-banner">The pinned instrument version has nothing to assess.</div>;
  const currentCrits = s.criteria.get(current.id) ?? [];
  const currentResp = s.blockResponses.get(current.block_key);
  const trigger = s.triggers.get(current.id);

  return (
    <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>
      <nav style={{ width: 230, flexShrink: 0, position: "sticky", top: "1rem" }}>
        <div className="card" style={{ padding: "0.5rem" }}>
          {s.blocks.map((b) => {
            const p = progress.get(b.block_key) ?? { done: 0, total: 0 };
            const isSel = b.block_key === current.block_key;
            return (
              <button
                key={b.id}
                onClick={() => setSelected(b.block_key)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.5rem",
                  width: "100%",
                  textAlign: "left",
                  padding: "0.35rem 0.5rem",
                  border: "none",
                  borderRadius: "var(--radius)",
                  background: isSel ? "var(--accent-soft)" : "transparent",
                  color: isSel ? "var(--accent-strong)" : "var(--text-secondary)",
                  cursor: "pointer",
                  fontSize: "var(--lf-meta-size)",
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {b.is_critical ? "▲ " : ""}
                  {b.title}
                </span>
                <span style={{ color: p.done === p.total && p.total > 0 ? "var(--state-ok)" : "var(--text-muted)", flexShrink: 0 }}>
                  {p.done}/{p.total}
                </span>
              </button>
            );
          })}
        </div>
        <div className="card" style={{ padding: "0.5rem", marginTop: "0.5rem" }}>
          <Link
            to={`/clients/${orgId}/runs/${runId}/results`}
            style={{ display: "block", padding: "0.35rem 0.5rem", fontSize: "var(--lf-meta-size)" }}
          >
            Evaluation results →
          </Link>
        </div>
      </nav>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "1rem" }}>
          <h1 style={{ marginBottom: 0 }}>
            {current.title}
            {current.is_critical && (
              <span className="badge" style={{ background: "var(--state-danger-soft)", color: "var(--state-danger)", marginLeft: "0.5rem", verticalAlign: "middle" }}>
                critical {noun.one}
              </span>
            )}
          </h1>
          <SaveIndicator save={save} />
        </div>
        {current.purpose && (
          <p style={{ color: "var(--text-muted)", marginTop: "0.25rem" }}>{current.purpose}</p>
        )}
        {s.readOnlyReason && (
          <div className="card" style={{ background: "var(--lf-amber-20)", color: "var(--lf-amber-deep)", marginBottom: "0.75rem" }}>
            Read-only: {s.readOnlyReason}.
          </div>
        )}

        <BlockControls
          block={current}
          allBlocks={s.blocks}
          naOffered={offersNotApplicable(currentCrits.map((c) => c.applicability_rule))}
          noun={noun}
          resp={currentResp ?? null}
          editable={s.editable}
          onPatch={(patch) => void patchBlock(current.block_key, patch)}
        />

        {trigger && (
          <TriggerCard
            trigger={trigger}
            resp={currentResp ?? null}
            editable={s.editable}
            onPatch={(patch) => void patchBlock(current.block_key, patch)}
          />
        )}

        {(currentResp?.applicability ?? "assessed") === "assessed" && (
          <section style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {currentCrits.map((c) => (
              <CriterionCard
                key={c.id}
                criterion={c}
                resp={s.criterionResponses.get(ck(current.block_key, c.criterion_key)) ?? null}
                evidence={s.evidence}
                links={s.links}
                anchors={s.anchors.get(c.scale) ?? null}
                editable={s.editable}
                onPatch={(patch) => void patchCriterion(current.block_key, c.criterion_key, patch)}
                onAttach={(evId) => void attachEvidence(current.block_key, c.criterion_key, evId)}
                onDetach={(respId, evId) => void detachEvidence(respId, evId)}
              />
            ))}
          </section>
        )}

        <EvidencePanel
          evidence={s.evidence}
          editable={s.editable}
          onAdd={(input) => void addEvidence(input)}
        />

        <p style={{ marginTop: "1rem" }}>
          <Link to={`/clients/${orgId}/runs/${runId}`}>← Back to run overview</Link>
        </p>
      </div>
    </div>
  );
}

function SaveIndicator({ save }: { save: SaveState }) {
  if (save.kind === "saving")
    return <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>saving…</span>;
  if (save.kind === "error")
    return (
      <span style={{ color: "var(--state-danger)", fontSize: "var(--lf-meta-size)" }}>
        not saved: {save.message}
      </span>
    );
  return <span style={{ color: "var(--state-ok)", fontSize: "var(--lf-meta-size)" }}>saved</span>;
}

// ---- Block-level applicability ----------------------------------------

function BlockControls({
  block,
  allBlocks,
  naOffered,
  noun,
  resp,
  editable,
  onPatch,
}: {
  block: InstrumentBlock;
  allBlocks: InstrumentBlock[];
  naOffered: boolean;
  noun: BlockNoun;
  resp: BlockResponse | null;
  editable: boolean;
  onPatch: (patch: Parameters<typeof updateBlockResponse>[1]) => void;
}) {
  const applicability = resp?.applicability ?? "assessed";
  const [rationale, setRationale] = useState(resp?.rationale ?? "");
  // "Combined" is chosen in two steps: the disposition, then its destination.
  // Gate 5 saves it only WITH the destination, so the first step is held here
  // until the second is made. (Before, the first step was a no-op that also
  // hid the destination picker, so the choice could not be completed.)
  const [choosingCombined, setChoosingCombined] = useState(false);
  useEffect(() => {
    setRationale(resp?.rationale ?? "");
    setChoosingCombined(false);
  }, [resp?.id, resp?.rationale, resp?.applicability, resp?.combined_into_block_id]);

  return (
    <div className="card" style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap" }}>
      <div className="field" style={{ marginBottom: 0 }}>
        <label htmlFor="block-applicability">{capitalised(noun.one)} applicability</label>
        <select
          id="block-applicability"
          value={choosingCombined ? "combined" : applicability}
          disabled={!editable}
          onChange={(e) => {
            const a = e.target.value as Applicability;
            if (a === "combined") {
              // Gate 5: combined saves only WITH its destination, picked below.
              setChoosingCombined(true);
              return;
            }
            setChoosingCombined(false);
            onPatch({ applicability: a, combined_into_block_id: null });
          }}
        >
          {APPLICABILITY_ORDER
            .filter((a) => a !== "not_applicable" || naOffered || applicability === a)
            .map((a) => (
              <option key={a} value={a}>{applicabilityLabel(a, noun)}</option>
            ))}
        </select>
      </div>
      {(applicability === "combined" || choosingCombined) && (
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="block-combined">Assessed within</label>
          <select
            id="block-combined"
            value={applicability === "combined" ? (resp?.combined_into_block_id ?? "") : ""}
            disabled={!editable}
            onChange={(e) =>
              e.target.value !== "" &&
              onPatch({ applicability: "combined", combined_into_block_id: e.target.value })
            }
          >
            <option value="">Choose the {noun.one} where this is assessed…</option>
            {allBlocks
              .filter((b) => b.block_key !== block.block_key)
              .map((b) => (
                <option key={b.id} value={b.block_key}>{b.title}</option>
              ))}
          </select>
        </div>
      )}
      <div className="field" style={{ marginBottom: 0, flex: "1 1 240px" }}>
        <label htmlFor="block-rationale">Applicability rationale</label>
        <input
          id="block-rationale"
          value={rationale}
          disabled={!editable}
          placeholder={applicability === "assessed" ? "(optional)" : "why this disposition"}
          onChange={(e) => setRationale(e.target.value)}
          onBlur={() => rationale !== (resp?.rationale ?? "") && onPatch({ rationale: rationale || null })}
        />
        <NoNamesHint />
      </div>
    </div>
  );
}

// ---- Trigger confirmation: three states, never inferred (ADR-24) -------

function TriggerCard({
  trigger,
  resp,
  editable,
  onPatch,
}: {
  trigger: InstrumentTrigger;
  resp: BlockResponse | null;
  editable: boolean;
  onPatch: (patch: Parameters<typeof updateBlockResponse>[1]) => void;
}) {
  const confirmed = resp?.trigger_confirmed ?? null;
  const [rationale, setRationale] = useState(resp?.trigger_rationale ?? "");
  useEffect(() => setRationale(resp?.trigger_rationale ?? ""), [resp?.id, resp?.trigger_rationale]);

  const states: { value: boolean | null; label: string }[] = [
    { value: null, label: "Not asked" },
    { value: false, label: "No" },
    { value: true, label: "Yes — confirmed" },
  ];

  return (
    <div className="card" style={{ marginTop: "0.75rem", borderLeft: "3px solid var(--state-danger)" }}>
      <h3 style={{ marginTop: 0 }}>Critical trigger</h3>
      <p style={{ marginTop: 0 }}>{trigger.trigger_text}</p>
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
        {states.map((st) => (
          <button
            key={String(st.value)}
            className={confirmed === st.value ? "btn" : "btn btn-secondary"}
            disabled={!editable}
            onClick={() => onPatch({ trigger_confirmed: st.value })}
          >
            {st.label}
          </button>
        ))}
        <input
          style={{ flex: "1 1 200px", padding: "0.45rem 0.6rem", border: "1px solid var(--border-strong)", borderRadius: "var(--radius)" }}
          placeholder="Trigger rationale"
          value={rationale}
          disabled={!editable}
          onChange={(e) => setRationale(e.target.value)}
          onBlur={() => rationale !== (resp?.trigger_rationale ?? "") && onPatch({ trigger_rationale: rationale || null })}
        />
        <NoNamesHint />
      </div>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)", marginBottom: 0 }}>
        A trigger fires only when you confirm it. Low scores never fire it for
        you, and “not asked” is recorded as exactly that.
      </p>
    </div>
  );
}

// ---- One criterion ------------------------------------------------------

function CriterionCard({
  criterion,
  resp,
  evidence,
  links,
  anchors,
  editable,
  onPatch,
  onAttach,
  onDetach,
}: {
  criterion: InstrumentCriterion;
  resp: CriterionResponse | null;
  evidence: EvidenceReference[];
  links: ResponseEvidenceLink[];
  anchors: ScaleLevel[] | null;
  editable: boolean;
  onPatch: (patch: Parameters<typeof updateCriterionResponse>[1]) => void;
  onAttach: (evidenceId: string) => void;
  onDetach: (responseId: string, evidenceId: string) => void;
}) {
  const applicability = resp?.applicability ?? "assessed";
  const score = resp?.score ?? null;
  const [rationale, setRationale] = useState(resp?.rationale ?? "");
  const [observation, setObservation] = useState(resp?.observation ?? "");
  const [attachOpen, setAttachOpen] = useState(false);
  const [changingApp, setChangingApp] = useState(false);
  useEffect(() => {
    setRationale(resp?.rationale ?? "");
    setObservation(resp?.observation ?? "");
  }, [resp?.id, resp?.rationale, resp?.observation]);

  const linked = resp
    ? links.filter((l) => l.criterion_response_id === resp.id)
    : [];
  const linkedEvidence = linked
    .map((l) => evidence.find((e) => e.id === l.evidence_reference_id))
    .filter((e): e is EvidenceReference => e !== undefined);
  const attachable = evidence.filter((e) => !linkedEvidence.some((le) => le.id === e.id));

  // The permitted scores come from the run's PINNED definition, never from a
  // constant here (0017 / the harmonized-scale ruling §6: the database range
  // is a storage envelope, the declared scale is the authority). `anchors` is
  // the `scales.<id>.levels[]` of the definition this run is locked to, so a
  // v1.0.0 run offers 0-4 and a v2.0.0 run offers 0-5 from the same code.
  const scoreValues = (anchors ?? [])
    .map((l) => l.value)
    .filter((v) => Number.isInteger(v))
    .sort((a, b) => a - b);

  // Fail closed. If the declared scale cannot be read we do NOT fall back to
  // a guessed range - inventing one here is exactly the hard-coded assumption
  // this change removes, and it would let an assessor record a score the
  // instrument never defined. Unscoring stays available; scoring does not.
  const scaleReadable = scoreValues.length > 0;

  // Keyboard efficiency (§11): with the card focused, the declared levels
  // score and Backspace/"-" unscores. A consultant records dozens of these per
  // run; the mouse must be optional. Keys only act where a click could - the
  // same editability and applicability rules, nothing bypassed. Single-digit
  // levels only: a two-digit level would have no single keystroke, and the
  // buttons remain the complete surface.
  function onKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!editable) return;
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA") return;
    const typed = Number(e.key);
    if (
      e.key.length === 1 &&
      Number.isInteger(typed) &&
      scoreValues.includes(typed) &&
      applicability === "assessed"
    ) {
      onPatch({ score: typed });
      e.preventDefault();
    } else if (e.key === "Backspace" || e.key === "-") {
      onPatch({ score: null });
      e.preventDefault();
    }
  }

  return (
    <div
      className="card"
      tabIndex={editable ? 0 : undefined}
      onKeyDown={onKey}
      aria-label={
        scaleReadable
          ? `Criterion ${criterion.criterion_key}: keys ${scoreValues[0]}-${scoreValues[scoreValues.length - 1]} score, Backspace clears`
          : `Criterion ${criterion.criterion_key}: scoring unavailable, scale not readable`
      }
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", alignItems: "baseline" }}>
        <div>
          <strong>{criterion.criterion_key}</strong>{" "}
          <span className="badge badge-role">{criterion.dimension}</span>
        </div>
        <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
          {/* Gate 3/4: unscored is its own state; scoring requires `assessed`. */}
          <button
            className={score === null ? "btn" : "btn btn-secondary"}
            disabled={!editable}
            title="Unscored - not the same as zero"
            onClick={() => onPatch({ score: null })}
          >
            —
          </button>
          {scoreValues.map((v) => (
            <button
              key={v}
              className={score === v ? "btn" : "btn btn-secondary"}
              disabled={!editable || applicability !== "assessed"}
              title={applicability !== "assessed" ? "A score needs applicability = assessed" : `score ${v}`}
              onClick={() => onPatch({ score: v })}
            >
              {v}
            </button>
          ))}
          {!scaleReadable && (
            <span style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
              scale not readable — scoring unavailable
            </span>
          )}
        </div>
      </div>
      {criterion.required_content && (
        <p style={{ color: "var(--text-secondary)", margin: "0.4rem 0" }}>{criterion.required_content}</p>
      )}
      {/* Tier-1 anchors from the pinned definition (§10.3: guidance in view,
          not noise) - the scale text travels inside the definition hash, so
          what the assessor reads here is what the run is locked to. */}
      <details style={{ margin: "0.25rem 0 0.5rem" }}>
        <summary style={{ cursor: "pointer", color: "var(--text-muted)", fontSize: "var(--lf-meta-size)" }}>
          Scoring anchors
        </summary>
        {anchors ? (
          <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "0.25rem 0.75rem", margin: "0.35rem 0 0", fontSize: "var(--lf-meta-size)" }}>
            {anchors.map((a) => (
              <span key={a.value} style={{ display: "contents" }}>
                <dt style={{ fontWeight: 600, color: "var(--text-secondary)" }}>
                  {a.value} — {a.state}
                </dt>
                <dd style={{ margin: 0, color: "var(--text-secondary)" }}>{a.anchor}</dd>
              </span>
            ))}
          </dl>
        ) : (
          <p style={{ fontSize: "var(--lf-meta-size)", color: "var(--text-muted)", margin: "0.35rem 0 0" }}>
            Scale: {criterion.scale}
          </p>
        )}
      </details>
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor={`app-${criterion.id}`}>Applicability</label>
          {/* Rarely changed: one line with "change…" (UI review slice 4). */}
          {!changingApp ? (
            <span>
              {applicabilityLabel(applicability, NEUTRAL_BLOCK_NOUN)}
              {editable && (
                <>
                  {" · "}
                  <button className="btn-link" onClick={() => setChangingApp(true)}>
                    change…
                  </button>
                </>
              )}
            </span>
          ) : (
            <select
              id={`app-${criterion.id}`}
              value={applicability}
              disabled={!editable}
              autoFocus
              onBlur={() => setChangingApp(false)}
              onChange={(e) => {
                const a = e.target.value as Applicability;
                if (a === "combined") return; // needs a destination; block-level combine covers R1
                setChangingApp(false);
                // Gate 4's UI half: leaving `assessed` clears the score in the
                // same save, because the database refuses the contradiction.
                onPatch(a === "assessed" ? { applicability: a } : { applicability: a, score: null });
              }}
            >
              {(["assessed", "not_applicable", "not_assessed", "excluded"] as Applicability[])
                .filter((a) => a !== "not_applicable" || offersNotApplicable([criterion.applicability_rule]) || applicability === a)
                .map((a) => (
                  <option key={a} value={a}>{applicabilityLabel(a, NEUTRAL_BLOCK_NOUN)}</option>
                ))}
            </select>
          )}
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}>
          <label htmlFor={`rat-${criterion.id}`}>Rationale</label>
          <input
            id={`rat-${criterion.id}`}
            value={rationale}
            disabled={!editable}
            onChange={(e) => setRationale(e.target.value)}
            onBlur={() => rationale !== (resp?.rationale ?? "") && onPatch({ rationale: rationale || null })}
          />
          <NoNamesHint />
        </div>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}>
          <label htmlFor={`obs-${criterion.id}`}>Observation</label>
          <input
            id={`obs-${criterion.id}`}
            value={observation}
            disabled={!editable}
            onChange={(e) => setObservation(e.target.value)}
            onBlur={() => observation !== (resp?.observation ?? "") && onPatch({ observation: observation || null })}
          />
          <NoNamesHint />
        </div>
      </div>
      <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
        {linkedEvidence.map((e) => (
          <span key={e.id} style={{ display: "inline-flex", alignItems: "center", gap: "0.15rem" }}>
            <EvidenceChip evidence={e} />
            {editable && resp && (
              <button
                title={`Withdraw "${e.label}" from this criterion (the evidence itself is kept)`}
                aria-label={`Unlink ${e.label}`}
                style={{ border: "none", background: "transparent", color: "var(--state-danger)", cursor: "pointer", padding: "0 0.2rem" }}
                onClick={() => onDetach(resp.id, e.id)}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {editable && (
          attachOpen ? (
            <select
              autoFocus
              onBlur={() => setAttachOpen(false)}
              onChange={(e) => {
                if (e.target.value) onAttach(e.target.value);
                setAttachOpen(false);
              }}
            >
              <option value="">Attach evidence…</option>
              {attachable.map((e) => (
                <option key={e.id} value={e.id}>{e.label}</option>
              ))}
            </select>
          ) : (
            <button className="btn btn-secondary" style={{ padding: "0.15rem 0.6rem", fontSize: "var(--lf-meta-size)" }} onClick={() => setAttachOpen(true)}>
              + evidence
            </button>
          )
        )}
      </div>
    </div>
  );
}

function EvidenceChip({ evidence }: { evidence: EvidenceReference }) {
  // ADR-32: a URL opens only on the user's own click, in a new tab with
  // noopener; a textual locator is NEVER rendered as a link.
  if (evidence.reference_kind === "url" && evidence.locator_normalized) {
    return (
      <a
        className="badge badge-role"
        href={evidence.locator_normalized}
        target="_blank"
        rel="noopener noreferrer"
        title={evidence.locator_original ?? evidence.label}
      >
        {evidence.label} ↗
      </a>
    );
  }
  return (
    <span className="badge badge-role" title={evidence.locator_original ?? undefined}>
      {evidence.label}
    </span>
  );
}

// ---- Run-level evidence -------------------------------------------------

function EvidencePanel({
  evidence,
  editable,
  onAdd,
}: {
  evidence: EvidenceReference[];
  editable: boolean;
  onAdd: (input: { evidence_type: string; label: string; reference_kind: EvidenceKind; locator_original: string | null }) => void;
}) {
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState<EvidenceKind>("locator");
  const [etype, setEtype] = useState("document");
  const [locator, setLocator] = useState("");

  return (
    <section className="card" style={{ marginTop: "1rem" }}>
      <h3 style={{ marginTop: 0 }}>Evidence for this run</h3>
      {evidence.length === 0 ? (
        <p style={{ color: "var(--text-muted)" }}>
          No evidence recorded yet. References are stored and displayed - the
          platform never fetches them.
        </p>
      ) : (
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
          {evidence.map((e) => (
            <EvidenceChip key={e.id} evidence={e} />
          ))}
        </div>
      )}
      {editable && (
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "flex-end", marginTop: "0.75rem" }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="ev-label">Label</label>
            <input id="ev-label" value={label} onChange={(e) => setLabel(e.target.value)} />
            <NoNamesHint />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="ev-kind">Kind</label>
            <select id="ev-kind" value={kind} onChange={(e) => setKind(e.target.value as EvidenceKind)}>
              <option value="locator">Textual locator</option>
              <option value="url">URL (https)</option>
              <option value="note">Note</option>
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="ev-type">Type</label>
            <select id="ev-type" value={etype} onChange={(e) => setEtype(e.target.value)}>
              {["document", "repository", "system_record", "dms_reference", "meeting", "decision", "note", "internal_link"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0, flex: "1 1 220px" }}>
            <label htmlFor="ev-loc">{kind === "note" ? "Note text" : kind === "url" ? "https:// address" : "Where it lives"}</label>
            <input id="ev-loc" value={locator} onChange={(e) => setLocator(e.target.value)} />
            <NoNamesHint />
          </div>
          <button
            className="btn"
            disabled={label.trim() === "" || (kind !== "note" && locator.trim() === "")}
            onClick={() => {
              onAdd({
                evidence_type: etype,
                label: label.trim(),
                reference_kind: kind,
                locator_original: locator.trim() === "" ? null : locator.trim(),
              });
              setLabel("");
              setLocator("");
            }}
          >
            Add evidence
          </button>
        </div>
      )}
    </section>
  );
}
